import React, {useEffect, useRef} from 'react';
import cytoscape from 'cytoscape';
import teachers from "./persons_withNameVariants.json";
import courseDict from "./course_id_to_name.json";
import {AutoComplete, Avatar, Badge, Divider, Input, List, Segmented, Space} from "antd";

const ConcentricGraph = ({jsonData}) => {
    // const HOST = 'http://localhost:8527'
    const HOST = 'https://fdeb.monsterlady.io';
    const containerRef = useRef(null);
    const cyRef = useRef(null); // 存储 Cytoscape 实例
    const THRESHOLD = 200; // 节点数量阈值
    const [detailNode, setDetailNode] = React.useState(null);
    const [optionKey, setOptionKey] = React.useState('');
    const [searchOptionsData, setSearchOptionsData] = React.useState([])
    const [searchOptions, setSearchOptions] = React.useState([]);
    const [sourceData, setSourceData] = React.useState([]);
    const [smallGraph, setSmallGraph] = React.useState(false);

    const clear = () => {
        setOptionKey('');
        setDetailNode(null);
        if (cyRef.current) {
            cyRef.current.edges().removeClass('hidden');
            cyRef.current.edges().forEach(edge => {
                if (edge.hasClass('to-delete')) {
                    edge.style({
                            opacity: 0
                        }
                    )
                } else {
                    edge.style({
                        'curve-style': () => {
                            //     check how many edges and nodes does cyRef.current have

                            const edges = cyRef.current.edges().length;
                            const nodes = cyRef.current.nodes().length;
                            if ((edges + nodes) <= THRESHOLD) {
                                return 'straight';
                            } else {
                                if (edge.id().startsWith('b')) {
                                    return 'straight';
                                }
                                return 'segments'
                            }
                        },
                        opacity: 0.5,
                        width: 0.5
                    })
                }
            })

        }
    }

    const getTeacherByUUID = (uuid) => {
        if (!uuid) {
            return '';
        }
        const teacher = teachers.find((teacher) => teacher.uuid === uuid);
        if (!teacher) {
            // console.log('Teacher not found', uuid);
            return null;
        }
        const firstName = teacher.name.firstName ?? '';
        const lastName = teacher.name.lastName ?? '';
        return `${firstName} ${lastName}`.trim();
    }

    const generateSearchOptions = (content) => {
        const data = []
        const courseNodes = {}
        const researcherNodes = {};
        content.forEach((researcher) => {

            if (researcher.partnerIndex) {
                const greyNode = {
                    id: `y - ${researcher.partnerUUID}`,
                    name: `${getTeacherByUUID(researcher.partnerUUID)}`,
                    type: 'staff'
                }
                data.push(greyNode);

                researcher.course.forEach((course) => {
                    courseNodes[course.courseId] = {
                        id: `b - ${course.courseId}`,
                        name: course.courseId,
                        type: 'teaching'
                    }
                });

                researcher.research.forEach((research) => {
                    researcherNodes[research.uuid] = {
                        id: `g - ${research.uuid}`,
                        name: research.title,
                        type: 'research',

                    };
                });
            }
        });

        // add the grey nodes to the data array
        Object.values(researcherNodes).forEach((node) => {
            data.push(node);
        });
        // add the course nodes to the data array
        Object.values(courseNodes).forEach((node) => {
            data.push(node);
        });
        setSearchOptionsData(data)

    }

    const generateData = (jsonData) => {
        const data = [];

        jsonData.content.forEach((researcher) => {
            const courseNodes = {}
            const researcherNodes = {};
            if (researcher.partnerIndex) {
                const greyNode = {
                    data: {
                        id: `y - ${researcher.partnerUUID}`,
                        level: 2,
                    },
                    "style": {
                        "background-color": "grey"
                    }
                }
                data.push(greyNode);

                researcher.course.forEach((course) => {
                    courseNodes[course.courseId] = {
                        data: {
                            id: `b - ${course.courseId}`,
                            courseId: course.courseId,
                            level: 3,
                        },
                        "style": {
                            "background-color": "blue"
                        }
                    }
                });

                researcher.research.forEach((research) => {
                    researcherNodes[research.uuid] = {
                        data: {
                            id: `g - ${research.uuid}`,
                            level: 1,
                            title: research.title,
                            url: research.url,
                            pureId: research.pureId
                        },
                        "style": {
                            "background-color": "green"
                        }
                    };
                });

                // add the grey nodes to the data array
                Object.values(researcherNodes).forEach((node) => {
                    data.push(node);
                    //      generate edge from grey to green
                    data.push({
                        data: {
                            "id": `g - ${node.data.id} - ${greyNode.data.id}`,
                            "source": node.data.id,
                            "target": greyNode.data.id
                        }
                    })
                });
                // add the course nodes to the data array
                Object.values(courseNodes).forEach((node) => {
                    data.push(node);
                    // generate edge from grey to course
                    data.push({
                        data: {
                            "id": `b - ${node.data.id} - ${greyNode.data.id}`,
                            "source": node.data.id,
                            "target": greyNode.data.id
                        }
                    });
                });
            }
        });

        // generate center node
        const centerNode = {
            data: {
                id: `c - ${jsonData.key}`,
                level: 4,
            },
            "style": {
                "background-color": "yellow"
            }
        }
        data.push(centerNode);

        // remove duplicates
        const uniqueNodes = new Set();
        const uniqueData = data.filter((item) => {
            if (uniqueNodes.has(item.data.id)) {
                return false; // 如果已经存在，则过滤掉
            }
            uniqueNodes.add(item.data.id); // 否则添加到集合中
            return true; // 保留该项
        })

        const nodes = [];
        const edges = [];

        uniqueData.forEach(node => {
            if (node.data.source) {
                edges.push({source: node.data.source, target: node.data.target});
            } else {
                nodes.push(node.data.id);
            }
        })
        if (nodes.length < THRESHOLD) {
            return uniqueData; // 如果节点少于10个，直接返回原始数据
        }
        const sortedNodes = sortNodes(nodes, edges);
        const sortedNodesData = sortedNodes.map(id => {
            return uniqueData.find(node => node.data.id === id);
        })
        const edgesData = [];
        const courseNodes = [];
        uniqueData.forEach(node => {
            if (node.data.source && node.data.target) {
                edgesData.push(node);
            } else if (node.data.id.startsWith('b')) {
                courseNodes.push(node);
            }
        })

        // 融合 edgesData 和 sortedNodesData, 合并两个list
        let final = [...sortedNodesData, ...courseNodes, ...edgesData, centerNode];
        // 去掉id相同的节点
        const uniqueIds = new Set();
        final = final.filter(item => {
            if (uniqueIds.has(item.data.id)) {
                return false; // 如果已经存在，则过滤掉
            } else {
                uniqueIds.add(item.data.id); // 否则添加到集合中
                return true; // 保留该项
            }
        });
        return final;
    }

    function sortNodes(nodes, edges) {
        // 拆分灰点 / 绿点
        const grayNodes = nodes.filter(id => id.startsWith('y'));
        const greenNodes = nodes.filter(id => id.startsWith('g'));

        // 1. 计算每个灰点的度数（只算到绿点的连边）
        const grayDegree = grayNodes.reduce((m, gid) => {
            m[gid] = 0;
            return m;
        }, {});
        edges.forEach(({source, target}) => {
            if (grayDegree.hasOwnProperty(source) && target.startsWith('g')) {
                grayDegree[source]++;
            }
            if (grayDegree.hasOwnProperty(target) && source.startsWith('g')) {
                grayDegree[target]++;
            }
        });

        // 2. 按度数降序排列灰点
        const sortedGray = grayNodes.slice().sort((a, b) =>
            grayDegree[b] - grayDegree[a]
        );

        // 3. 为每个绿点确定“主归属”灰点
        const primary = {};
        greenNodes.forEach(gid => {
            // 统计它连到每个灰点的条数
            const counts = sortedGray.reduce((m, yid) => {
                m[yid] = 0;
                return m;
            }, {});
            edges.forEach(({source, target}) => {
                if (source === gid && counts.hasOwnProperty(target)) {
                    counts[target]++;
                }
                if (target === gid && counts.hasOwnProperty(source)) {
                    counts[source]++;
                }
            });
            // 选出连边数最多的灰点；若都没连，则分给最后一个灰点
            const maxCount = Math.max(...Object.values(counts));
            if (maxCount > 0) {
                // 若有多个并列，用灰点整体度数作二次排序
                const candidates = Object.entries(counts)
                    .filter(([_, c]) => c === maxCount)
                    .map(([yid]) => yid);
                primary[gid] = candidates.sort((a, b) =>
                    grayDegree[b] - grayDegree[a]
                )[0];
            } else {
                primary[gid] = sortedGray[sortedGray.length - 1];
            }
        });

        // 4. 按主归属把绿点分组
        const groups = sortedGray.reduce((m, yid) => {
            m[yid] = [];
            return m;
        }, {});
        greenNodes.forEach(gid => {
            groups[primary[gid]].push(gid);
        });

        // 5. 合并：先放灰点，再依次放每个灰点组内的绿点
        const result = [];
        sortedGray.forEach(yid => {
            result.push(yid);
            result.push(...groups[yid]);
        });

        return result;
    }

    const DetailDisplay = ({node, centerId}) => {
        if (!node) {
            const staff = teachers.find((teacher) => teacher.uuid === centerId.split('c - ')[1])
            const name = `${staff.name.firstName ?? ''} ${staff.name.lastName ?? ''}`.trim();
            const department = staff.departments ?? '';
            const active = staff.active ? 'In Service' : 'Leave';

            const coursesData = cyRef.current.nodes().filter(node => node.id().startsWith('b') && !node.id().includes('dummy')).map(node => node.data().courseId)
            const researchData = cyRef.current.nodes().filter(node => node.id().startsWith('g') && !node.id().includes('dummy')).map(node => node.data())
            const staffData = cyRef.current.nodes().filter(node => node.id().startsWith('y') && !node.id().includes('dummy')).map(node => {
                const uuid = node.id().split('y - ')[1];
                const teacher = teachers.find((teacher) => teacher.uuid === uuid);
                if (!teacher) {
                    // console.log('Teacher not found', uuid);
                    return null;
                }
                const firstName = teacher.name.firstName ?? '';
                const lastName = teacher.name.lastName ?? '';
                return {
                    id: node.id(),
                    name: `${firstName} ${lastName}`.trim(),
                    active: teacher.active ? 'In Service' : 'Leave',
                    department: teacher.departments
                }
                // return getTeacherByUUID(uuid[1]);
            })

            const courseCount = coursesData.length;
            const researchCount = researchData.length;
            const staffCount = staffData.length;
            return <div>
                <div style={{display: "flex", gap: "10px"}}>
                    <h3>{`${name} - ${active}`}</h3>
                    <h3>{department}</h3>
                </div>

                <Divider orientation="left">{`Collaborative Researchers: ${staffCount}`}</Divider>
                <div
                    style={{
                        height: 70,
                        overflow: "auto",
                        border: "1px solid #f0f0f0",
                        borderRadius: 8,
                    }}
                >
                    <List
                        size="small"
                        bordered
                        dataSource={staffData}
                        renderItem={(node) => <List.Item
                            key={`c ${node.name} `} // 确保有唯一 key
                            onMouseEnter={() => {
                                if (smallGraph) {
                                    cyRef.current.edges().addClass('hidden');
                                    cyRef.current.getElementById(node.id).connectedEdges().filter(edge => !edge.id().includes('dummy')).removeClass('hidden');
                                    cyRef.current.getElementById(node.id).connectedEdges().filter(edge => !edge.id().includes('dummy')).style({
                                        // TODO make it straight line
                                        'curve-style': 'straight',
                                        opacity: 1,
                                        width: 16,
                                    })
                                } else {
                                    cyRef.current.getElementById(node.id).connectedEdges().filter(edge => !edge.id().includes('dummy')).removeClass('to-delete');
                                    cyRef.current.getElementById(node.id).connectedEdges().filter(edge => !edge.id().includes('dummy')).style({
                                        // TODO make it straight line
                                        'curve-style': 'straight',
                                        opacity: 1,
                                        width: 16,
                                    })
                                    //TODO make other blue edges hidden
                                    cyRef.current.edges().forEach(edge => {
                                        if (edge.id().startsWith('b') && edge.style('width') !== '16px') {
                                            edge.addClass('hidden');
                                        }
                                    })
                                    cyRef.current.edges().forEach(edge => {
                                        if (edge.style('curve-style') === 'segments') {
                                            edge.addClass('hidden');
                                        }
                                    });
                                }
                            }

                            }
                            onMouseLeave={() => {
                                if (smallGraph) {
                                    cyRef.current.edges().removeClass('hidden');
                                    cyRef.current.getElementById(node.id).connectedEdges().filter(edge => !edge.id().includes('dummy')).style({
                                        // TODO make it straight line
                                        'curve-style': 'straight',
                                        opacity: 0.5,
                                        width: 0.5,
                                    })
                                } else {
                                    cyRef.current.getElementById(node.id).connectedEdges().filter(edge => !edge.id().includes('dummy') && !edge.id().startsWith('b')).addClass('to-delete');
                                    cyRef.current.getElementById(node.id).connectedEdges().filter(edge => !edge.id().includes('dummy') && !edge.id().startsWith('b')).style({
                                        opacity: 0,
                                    })
                                    cyRef.current.getElementById(node.id).connectedEdges().filter(edge => edge.id().startsWith('b')).style({
                                        'curve-style': 'straight',
                                        opacity: 0.5,
                                        width: 0.5,
                                    })
                                    cyRef.current.edges().forEach(edge => {
                                        if (edge.id().startsWith('b') && edge.style('width') === '0.5px') {
                                            edge.removeClass('hidden');
                                        }
                                    })
                                    cyRef.current.edges().forEach(edge => {
                                        if (edge.style('curve-style') === 'segments') {
                                            edge.removeClass('hidden');
                                        }
                                    });
                                    // cyRef.current.getElementById(node.id).connectedEdges().filter(edge => !edge.id().includes('dummy')).addClass('hidden');
                                }
                            }}
                        ><Space size="small" wrap>
                            <Badge
                                status={node.active === "In Service" ? "success" : "default"}
                                text={node.active}
                            />
                            <span>{`- ${node.name}`}</span>
                            <span>{` - ${node.department}`}</span>

                        </Space></List.Item>}
                    />
                </div>

                <Divider orientation="left">{`Research Papers: ${researchCount}`}</Divider>
                <div
                    style={{
                        height: 70,
                        overflow: "auto",
                        border: "1px solid #f0f0f0",
                        borderRadius: 8,
                    }}
                >
                    <List
                        size="small"
                        bordered
                        dataSource={researchData}
                        renderItem={(node) => <List.Item
                            key={`c ${node.id}`}
                            onMouseEnter={() => {
                                if (smallGraph) {
                                    cyRef.current.edges().addClass('hidden');
                                    cyRef.current.getElementById(node.id).connectedEdges().filter(edge => !edge.id().includes('dummy')).removeClass('hidden');
                                    cyRef.current.getElementById(node.id).connectedEdges().filter(edge => !edge.id().includes('dummy')).style({
                                        // TODO make it straight line
                                        'curve-style': 'straight',
                                        opacity: 1,
                                        width: 16,
                                    })
                                } else {
                                    cyRef.current.getElementById(node.id).connectedEdges().filter(edge => !edge.id().includes('dummy')).removeClass('to-delete');
                                    cyRef.current.getElementById(node.id).connectedEdges().filter(edge => !edge.id().includes('dummy')).style({
                                        // TODO make it straight line
                                        'curve-style': 'straight',
                                        opacity: 1,
                                        width: 16,
                                    })
                                    cyRef.current.edges().forEach(edge => {
                                        if (edge.id().startsWith('b')) {
                                            edge.addClass('hidden');
                                        }
                                    })
                                    cyRef.current.edges().forEach(edge => {
                                        if (edge.style('curve-style') === 'segments') {
                                            edge.addClass('hidden');
                                        }
                                    });
                                }

                            }}
                            onMouseLeave={() => {
                                if (smallGraph) {
                                    cyRef.current.edges().removeClass('hidden');
                                    cyRef.current.getElementById(node.id).connectedEdges().filter(edge => !edge.id().includes('dummy')).style({
                                        // TODO make it straight line
                                        'curve-style': 'straight',
                                        opacity: 0.5,
                                        width: 0.5,
                                    })
                                } else {
                                    cyRef.current.getElementById(node.id).connectedEdges().filter(edge => !edge.id().includes('dummy')).addClass('to-delete');
                                    cyRef.current.getElementById(node.id).connectedEdges().filter(edge => !edge.id().includes('dummy')).style({
                                        opacity: 0,
                                    })
                                    cyRef.current.edges().forEach(edge => {
                                        if (edge.id().startsWith('b')) {
                                            edge.removeClass('hidden');
                                        }
                                    })
                                    cyRef.current.edges().forEach(edge => {
                                        if (edge.style('curve-style') === 'segments') {
                                            edge.removeClass('hidden');
                                        }
                                    });
                                }
                            }}
                        >{<a href={node.url} target="_blank"
                             rel="noopener noreferrer">
                            {node.title}
                        </a>}</List.Item>}
                    />
                </div>
                <Divider orientation="left">{`Courses: ${courseCount}`}</Divider>
                <div
                    style={{
                        height: 70,
                        overflow: "auto",
                        border: "1px solid #f0f0f0",
                        borderRadius: 8,
                    }}
                >
                    <List
                        size="small"
                        bordered
                        dataSource={coursesData}
                        renderItem={(node) => <List.Item
                            key={`c ${node} ${centerId}`}
                            onMouseEnter={() => {
                                const id = `b - ${node}`;
                                cyRef.current.edges().addClass('hidden');
                                cyRef.current.getElementById(id).connectedEdges().filter(edge => !edge.id().includes('dummy')).removeClass('hidden');
                                cyRef.current.getElementById(id).connectedEdges().filter(edge => !edge.id().includes('dummy')).style({
                                    // TODO make it straight line
                                    'curve-style': 'straight',
                                    opacity: 1,
                                    width: 16,
                                })
                            }}
                            onMouseLeave={() => {
                                const id = `b - ${node}`;
                                cyRef.current.edges().removeClass('hidden');
                                cyRef.current.getElementById(id).connectedEdges().filter(edge => !edge.id().includes('dummy')).style({
                                    // TODO make it straight line
                                    'curve-style': 'straight',
                                    opacity: 0.5,
                                    width: 0.5,
                                })
                            }}
                        >{`${node} - ${courseDict[node]}`}</List.Item>}
                    />
                </div>
            </div>

        }
        const id = node.data.id;
        if (id.startsWith('y')) {
            const staff = teachers.find((teacher) => teacher.uuid === id.split('y - ')[1])
            const name = `${staff.name.firstName ?? ''} ${staff.name.lastName ?? ''}`.trim();
            const department = staff.departments ?? '';
            const active = staff.active ? 'In Service' : 'Leave';
            const connectedEdges = cyRef.current.getElementById(id).connectedEdges();
            const teachingCoursesCount = connectedEdges
                .filter(edge => edge.id().startsWith('b')).length;
            const researchPapersCount = connectedEdges.filter(edge => edge.id().startsWith('g')).length;
            let researchData = [];
            if (researchPapersCount > 0) {
                researchData = connectedEdges.filter(edge => edge.id().startsWith('g')).map(edge => {
                    const id = edge.id();
                    const researchId = id.split(' - ')[2].trim();
                    return sourceData.find((edge) => edge.data.id === `g - ${researchId}`).data;
                })
            }
            let coursesData = [];
            if (teachingCoursesCount > 0) {
                coursesData = connectedEdges.filter(edge => edge.id().startsWith('b')).map(edge => {
                    const id = edge.id();
                    const courseId = id.split(' - ')[2].trim();
                    return sourceData.find((edge) => edge.data.id === `b - ${courseId}`).data;
                })
            }

            return (
                <div>
                    <div style={{display: "flex", gap: "10px"}}>
                        <h3>{`${name} - ${active}`}</h3>
                        <h3>{department}</h3>
                    </div>
                    {/*<div style={{display: "flex", gap: "10px"}}>*/}
                    {/*    <h4>{`Research Papers Count: ${researchPapersCount}`}</h4>*/}
                    {/*    <h4>{`Courses Count: ${teachingCoursesCount}`}</h4>*/}
                    {/*</div>*/}
                    {/*/!* TODO adding a scrollable list of courses and research papers*!/*/}
                    <Divider orientation="left">{`Research Papers: ${researchPapersCount}`}</Divider>
                    <div
                        style={{
                            height: 135,
                            overflow: "auto",
                            border: "1px solid #f0f0f0",
                            borderRadius: 8,
                        }}
                    >
                        <List
                            size="small"
                            bordered
                            dataSource={researchData}
                            renderItem={(node) => <List.Item
                                key={node.id} // 确保有唯一 key
                                onMouseEnter={() => {
                                    console.log(`Mouse entered on node: ${node.id}`);
                                    // const edge = cyRef.current.getElementById(`g - g - ${node.id} - ${id}`);
                                    //     TODO highlight the edge and hidden other edges
                                    cyRef.current.getElementById(id).connectedEdges().filter(edge => !edge.id().includes('dummy') && edge.id() !== `g - ${node.id} - ${id}`).addClass('hidden');

                                }}
                                onMouseLeave={() => {
                                    console.log(`Mouse left node: ${node.id}`);
                                    cyRef.current.getElementById(id).connectedEdges().filter(edge => !edge.id().includes('dummy') && edge.id() !== `g - ${node.id} - ${id}`).removeClass('hidden');
                                }}
                            >{<a href={node.url} target="_blank"
                                 rel="noopener noreferrer">
                                {node.title}
                            </a>}</List.Item>}
                        />
                    </div>
                    <Divider orientation="left">{`Courses: ${teachingCoursesCount}`}</Divider>
                    <div
                        style={{
                            height: 120,
                            overflow: "auto",
                            border: "1px solid #f0f0f0",
                            borderRadius: 8,
                        }}
                    >
                        <List
                            size="small"
                            bordered
                            dataSource={coursesData}
                            renderItem={(node) => <List.Item
                                key={node.id} // 确保有唯一 key
                                onMouseEnter={() => {
                                    console.log(`Mouse entered on node: ${node.id}`);
                                    // const edge = cyRef.current.getElementById(`g - g - ${node.id} - ${id}`);
                                    //     TODO highlight the edge and hidden other edges
                                    cyRef.current.getElementById(id).connectedEdges().filter(edge => !edge.id().includes('dummy') && edge.id() !== `b - ${node.id} - ${id}`).addClass('hidden');

                                }}
                                onMouseLeave={() => {
                                    console.log(`Mouse left node: ${node.id}`);
                                    cyRef.current.getElementById(id).connectedEdges().filter(edge => !edge.id().includes('dummy') && edge.id() !== `b - ${node.id} - ${id}`).removeClass('hidden');
                                }}
                            >{`${node.courseId} - ${courseDict[node.courseId]}`}</List.Item>}
                        />
                    </div>
                    {/*<Divider orientation="left">Large Size</Divider>*/}
                    {/*<List*/}
                    {/*    size="small"*/}
                    {/*    header={<div>Header</div>}*/}
                    {/*    footer={<div>Footer</div>}*/}
                    {/*    bordered*/}
                    {/*    dataSource={()=>{*/}
                    {/*        console.log(connectedEdges*/}
                    {/*            .filter(edge => edge.id().startsWith('g')).map((edge) => edge.data))*/}
                    {/*        return [*/}
                    {/*            "Course Test1",*/}
                    {/*            "Course Test2",*/}
                    {/*            "Course Test3"*/}
                    {/*        ]*/}
                    {/*    }}*/}
                    {/*    renderItem={(item) => <List.Item>{item}</List.Item>}*/}
                    {/*/>*/}
                </div>
            );
        }
        if (id.startsWith('g')) {
            const connectedEdges = cyRef.current.getElementById(id).connectedEdges();
            const staffId = connectedEdges.filter(edge => edge.id().startsWith('g') && !edge.id().includes('dummy')).map(edge => {
                const id = edge.id();
                const staff_id = id.split(' - ')[4].trim();
                return `y - ${staff_id}`;
            })
            const staffData = cyRef.current.nodes().filter(node => staffId.includes(node.id())).map(node => {
                const uuid = node.id().split('y - ')[1];
                const teacher = teachers.find((teacher) => teacher.uuid === uuid);
                if (!teacher) {
                    // console.log('Teacher not found', uuid);
                    return null;
                }
                const firstName = teacher.name.firstName ?? '';
                const lastName = teacher.name.lastName ?? '';
                return {
                    id: node.id(),
                    name: `${firstName} ${lastName}`.trim(),
                    active: teacher.active ? 'In Service' : 'Leave',
                    department: teacher.departments
                }
            })
            const researchStaffCount = staffData.length;
            return <div>
                <h3><a href={node.data.url} target="_blank" rel="noopener noreferrer">
                    {node.data.title}
                </a></h3>
                <Divider orientation="left">{`Collaborative Researchers: ${researchStaffCount}`}</Divider>
                <div
                    style={{
                        height: 130,
                        overflow: "auto",
                        border: "1px solid #f0f0f0",
                        borderRadius: 8,
                    }}
                >
                    <List
                        size="small"
                        bordered
                        dataSource={staffData}
                        renderItem={(node) => <List.Item
                            key={`c ${node.name} `} // 确保有唯一 key
                            onMouseEnter={() => {
                                cyRef.current.getElementById(id).connectedEdges().filter(edge => !edge.id().includes('dummy') && edge.id() !== `g - ${id} - ${node.id}`).addClass('hidden');

                            }}
                            onMouseLeave={() => {
                                console.log(`Mouse left node: ${node.id}`);
                                cyRef.current.getElementById(id).connectedEdges().filter(edge => !edge.id().includes('dummy') && edge.id() !== `g - ${id} - ${node.id}`).removeClass('hidden');
                            }}
                        ><Space size="small" wrap>
                            <Badge
                                status={node.active === "In Service" ? "success" : "default"}
                                text={node.active}
                            />
                            <span>{`- ${node.name}`}</span>
                            <span>{` - ${node.department}`}</span>

                        </Space></List.Item>}
                    />
                </div>
            </div>
        }
        if (id.startsWith('b')) {
            const connectedEdges = cyRef.current.getElementById(id).connectedEdges();
            const teachingCoursesCount = connectedEdges.length;
            const staffId = connectedEdges.filter(edge => edge.id().startsWith('b') && !edge.id().includes('dummy')).map(edge => {
                const id = edge.id();
                const staff_id = id.split(' - ')[4].trim();
                return `y - ${staff_id}`;
            })
            const staffData = cyRef.current.nodes().filter(node => staffId.includes(node.id())).map(node => {
                const uuid = node.id().split('y - ')[1];
                const teacher = teachers.find((teacher) => teacher.uuid === uuid);
                if (!teacher) {
                    // console.log('Teacher not found', uuid);
                    return null;
                }
                const firstName = teacher.name.firstName ?? '';
                const lastName = teacher.name.lastName ?? '';
                return {
                    id: node.id(),
                    name: `${firstName} ${lastName}`.trim(),
                    active: teacher.active ? 'In Service' : 'Leave',
                    department: teacher.departments
                }
            })
            return <div>
                <h3>{`${node.data.courseId} - ${courseDict[node.data.courseId]} `}</h3>
                {/*<h4>{`Cooperative Staff: ${teachingCoursesCount}`}</h4>*/}
                <Divider orientation="left">{`Collaborative Researchers: ${teachingCoursesCount}`}</Divider>
                <div
                    style={{
                        height: 130,
                        overflow: "auto",
                        border: "1px solid #f0f0f0",
                        borderRadius: 8,
                    }}
                >
                    <List
                        size="small"
                        bordered
                        dataSource={staffData}
                        renderItem={(node) => <List.Item
                            key={`c ${node.name} `} // 确保有唯一 key
                            onMouseEnter={() => {
                                cyRef.current.getElementById(id).connectedEdges().filter(edge => !edge.id().includes('dummy') && edge.id() !== `b - ${id} - ${node.id}`).addClass('hidden');

                            }}
                            onMouseLeave={() => {
                                console.log(`Mouse left node: ${node.id}`);
                                cyRef.current.getElementById(id).connectedEdges().filter(edge => !edge.id().includes('dummy') && edge.id() !== `b - ${id} - ${node.id}`).removeClass('hidden');
                            }}
                        ><Space size="small" wrap>
                            <Badge
                                status={node.active === "In Service" ? "success" : "default"}
                                text={node.active}
                            />
                            <span>{`- ${node.name}`}</span>
                            <span>{` - ${node.department}`}</span>

                        </Space></List.Item>}
                    />
                </div>
            </div>
        }
    }

    const handleSearchBarSelect = (id) => {
        const node = cyRef.current.$id(id);
        cyRef.current.animate({
            center: {eles: node},
            zoom:  0.8,
            duration: 3000
        });
    }

    const handleSearchBarSearch = (value) => {
        const filteredNode = searchOptionsData.filter((node) => {
            return node.id.toLowerCase().includes(value.toLowerCase()) || node.name.toLowerCase().includes(value.toLowerCase())
        }).map((node) => {
            return {
                value: node.id, // 用 UUID 作为 key
                label: (<div style={{
                    color: node.type === 'research' ? 'green' : node.type === 'teaching' ? 'blue' : 'grey',
                }}>
                    <span>{node.name}</span>
                </div>)
            };
        })

        setSearchOptions(filteredNode);
    }


    useEffect(() => {
        generateSearchOptions(jsonData.content)
        // 1. 销毁旧实例
        if (cyRef.current) {
            cyRef.current.destroy();
            cyRef.current = null;
        }

        let isReady = false
        // console.log(JSON.stringify(jsonData, null, 2));
        const ddd = generateData(jsonData);

        setSourceData(ddd);

        const cy = cytoscape({
                container: containerRef.current,
                elements: ddd,
                style: [
                    {
                        selector: 'node',
                        style: {
                            label: (ele) => {
                                if (ele.id().startsWith('c')) {
                                    return getTeacherByUUID(jsonData.key)
                                }
                                if (ele.id().startsWith('b')) {
                                    return ele.data('id').split('b - ')[1];
                                }
                                const uuid = ele.id().split('y - ');
                                return getTeacherByUUID(uuid[1])
                            },
                            width: 30,
                            height: 30,
                            'text-valign': 'center',
                            'text-halign': 'center',
                            'color': '#fff',
                            'font-size': 10,
                            'text-outline-color': '#000',
                            'text-outline-width': 1,
                            drag: "no",
                            shape: (ele) => {
                                if (ele.id().startsWith('c')) {
                                    return 'triangle';
                                }
                                return 'ellipse';
                            },
                        }
                    },
                    {
                        selector: 'node.dummy-node',
                        style: {
                            'opacity': 0,
                            'background-opacity': 0,
                            'border-opacity': 0,
                            'label': '',
                            'width': 1,
                            'height': 1
                        }
                    },
                    {
                        selector: 'edge',
                        style: {
                            'width': 0.5,
                            'line-color': (ele) => {
                                if (ele.id().startsWith('b') || ele.id().startsWith('dummy_b')) {
                                    return 'blue';
                                }
                                if (ele.id().startsWith('g') || ele.id().startsWith('dummy_g')) {
                                    return 'green';
                                }
                            },
                            'target-arrow-color': '#ccc',
                            'curve-style': (ele) => {
                                if (ddd.length <= THRESHOLD) {
                                    return 'straight';

                                } else {
                                    if (ele.data('id').startsWith('b')) {
                                        return 'straight';
                                    }
                                    return 'segments'
                                }
                            },
                            opacity: 0.5,
                            events: "no",
                        }
                    },
                    {
                        selector: "edge.hidden",
                        style: {
                            opacity: 0,
                            visibility: "hidden",
                        },
                    },
                    {
                        selector: "edge.to-delete",
                        style: {
                            opacity: 0,
                            // visibility: "hidden",
                        },
                    },
                    {
                        selector: "edge.highlighted",
                        style: {
                            'curve-style': 'straight',
                            'line-color': (ele) => {
                                if (ele.id().startsWith('b') || ele.id().startsWith('dummy_b')) {
                                    return 'blue';
                                }
                                if (ele.id().startsWith('g') || ele.id().startsWith('dummy_g')) {
                                    return 'green';
                                }
                                return '#ccc';
                            },
                            width: 16,
                            "line-style": "solid",
                        },
                    },
                ],
                minZoom: 0.05,           // 允许的最小缩放
                maxZoom: 0.85,             // 允许的最大缩放
                wheelSensitivity: 0.75,  // ↓ 值越小，滚轮一次缩放得越“细”（更慢更平滑）
            }
        );

        cyRef.current = cy;

        const layout = cy.layout(
            {
                name: 'concentric',
                concentric: function (node) {
                    return node.data('level'); // 越大越靠近中心
                },
                levelWidth: function (nodes) {
                    return 1; // 每一圈只包含同 level 的节点
                },
                spacingFactor: 2,

            }
        );

        const onReady = () => {
            console.log('Layout is ready');
            isReady = true;
            cy.nodes().lock();
        }

        const onStop = async () => {
            // // 如果已经执行过，就直接跳过
            // if (didStopRef.current) return;
            // didStopRef.current = true;
            if (!isReady) return;
            if (ddd.length <= THRESHOLD) {
                setSmallGraph(true);
                cy.style().selector('edge').style({
                    'curve-style': 'straight',
                    width: 0.5,
                    opacity: 0.5,
                }).update();
                console.log('Node count is below threshold, skipping fast bundle');
                return;
            }
            const levelToRadii = {};
            const centerX = cy.width() / 2;
            const centerY = cy.height() / 2;
            console.log('Calculating average radii for each level...', centerX, centerY);

            // // 添加一个新的节点 centerNode 作为中心节点
            // cy.add({
            //
            //     group: 'nodes',
            //     data: {id: 'centerNode'},
            //     position: {x: centerX, y: centerY}
            // });

            cy.nodes().forEach(node => {
                const level = node.data('level');
                const pos = node.position();
                const r = Math.hypot(pos.x - centerX, pos.y - centerY);

                if (!levelToRadii[level]) levelToRadii[level] = [];
                levelToRadii[level].push(r);
            });
            console.log(levelToRadii)

            const levelToAvgRadius = {};
            for (const level in levelToRadii) {
                const arr = levelToRadii[level];
                levelToAvgRadius[level] = arr.reduce((a, b) => a + b, 0) / arr.length;
            }
            console.log('Average radii by level:', levelToAvgRadius);
            //
            // 获取所有节点的坐标
            const positions = cy.nodes().map(node => ({
                id: node.id(),
                level: node.data('level'),
                x: node.position('x'),
                y: node.position('y'),
            }));
            // 获取所有边的连接信息
            const edges = cy.edges()
                .filter(edge => !edge.id().startsWith('b'))
                .map(edge => ({
                    id: edge.id(),
                    source: edge.source().id(),
                    target: edge.target().id()
                }));
            const body = {
                nodes: positions,
                edges: edges,
                center: [centerX, centerY],
                radius_dict: levelToAvgRadius,
                factor: 0.65
            }
            const response = await fetch(`${HOST}/dummy-nodes-and-edges`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(body)
            });
            const data = await response.json();
            console.log('Fast Bundle Response:', data);
            const edges_to_delete = data.edges_to_delete;
            const nodes_to_add = data.nodes_to_add;
            const edges_to_add = data.edges_to_add;

            edges_to_delete.forEach(edgeId => {
                const edge = cy.getElementById(edgeId);
                if (edge) {
                    edge.addClass('to-delete');
                }
            })
            nodes_to_add.forEach(nodeData => {
                cy.add({
                    classes: 'dummy-node',
                    group: 'nodes',
                    data: nodeData,
                    position: {x: nodeData.x, y: nodeData.y}
                });
            });
            edges_to_add.forEach(edgeData => {
                cy.add({
                    group: 'edges',
                    data: edgeData
                });
            });

            //TODO 重新获取所有节点和边的连接信息

            const nwPositions = cy.nodes()
                .map(node => ({
                    id: node.id(),
                    level: node.data('level'),
                    x: node.position('x'),
                    y: node.position('y'),
                }));

            console.log(cy.edges().length, 'edges before fast bundle');

            const nwEdges = cy.edges()
                .filter(edge => !edge.hasClass('to-delete') && !edge.id().startsWith('b'))
                .map(edge => ({
                    id: edge.id(),
                    source: edge.source().id(),
                    target: edge.target().id()
                }));
            console.log(nwEdges.length, 'edges after fast bundle');

            const body2 = {
                nodes: nwPositions,
                edges: nwEdges,
                params: {
                    bandwidth: 0.05,            // 适当增加带宽
                    decay: 0.85,
                    iterations: 150,
                    union: true
                }
            };

            const response2 = await fetch(`${HOST}/fast-bundle`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(body2)
            });
            const data2 = await response2.json();
            console.log('Fast Bundle Response:', data2);

            // const dummyEdges = [];
            data2.paths.forEach(({edgeId, segmentWeights, segmentDistances}) => {
                const edge = cy.getElementById(edgeId);
                // if(edgeId.startsWith('dummy')){
                //     //跳过
                //     dummyEdges.push(edge);
                //     // return;
                // }
                // 设置 segmentWeights 和 segmentDistances
                edge.data('segmentWeights', segmentWeights);
                edge.data('segmentDistances', segmentDistances);
            });
            cy.style().selector('edge').style({
                'curve-style': (ele) => {

                    if (ele.data('id').startsWith('b')) {
                        return 'straight';
                    }
                    return 'segments'

                },
                'segment-weights': 'data(segmentWeights)',
                'segment-distances': 'data(segmentDistances)',
                'edge-distances': 'node-position'
            }).update();
            // const origWarn = console.warn;
            // console.warn = function(msg, ...rest) {
            //     // 只拦截 Cytoscape 那种 “invalid endpoints” 警告
            //     if (typeof msg === 'string'
            //         && msg.startsWith('Edge `')
            //         && msg.includes('invalid endpoints')
            //     ) {
            //         // 抽取 edgeId
            //         const m = msg.match(/^Edge `(.+?)`/);
            //         if (m) {
            //             const badId = m[1];
            //             const edge  = cyRef.current.getElementById(badId);
            //             if (edge.length) {
            //                 // console.log("Found bad edge:", badId);
            //                 // 获取当前它要用的 segmentWeights（可能是 data 或 style 里读出来的）
            //                 // const weights = edge.data('segmentWeights') || [];
            //                 // 立刻把 distances 全部置零
            //                 edge.style({
            //                     'segment-distances': [0],
            //                     // 保留 weights，让它仍然用 segments
            //                     'segment-weights':   [0.5]
            //                 });
            //             }
            //         }
            //     }
            //     // 不干扰其它 warn
            //     origWarn.call(console, msg, ...rest);
            // };

            console.log('Layout has stopped');
        }

        layout.on('layoutready', onReady);

        layout.on('layoutstop', onStop)

        layout.run();

        cy.on('tap', 'node', (event) => {
            const node = event.target;
            console.log(`Node ${node.id()} tapped`);
            if (node.id().startsWith('c')) {
                return;
            }

            setDetailNode(ddd.find(item => item.data.id === node.id()));
            // 获取所有跟该节点相关的边
            cy.edges().removeClass('hidden')
            const edges = node.connectedEdges();
            cy.edges().addClass('hidden');
            edges.removeClass('hidden');
            // edges.addClass('highlighted');
            edges.forEach(edge => {
                if (edge.id().includes('dummy')) {
                    edge.addClass('hidden');
                } else {
                    // if(edge.hasClass('to-delete')){
                    //     edge.style({
                    //         // TODO make it straight line
                    //         'curve-style': 'straight',
                    //         opacity: 1,
                    //         width: 16,
                    //     })
                    // } else {
                    //     edge.addClass('highlighted');
                    // }
                    // edge.style({
                    //     // TODO make it straight line
                    //     'curve-style': 'straight',
                    //     opacity: 1,
                    // })
                    edge.style({
                        // TODO make it straight line
                        'curve-style': 'straight',
                        opacity: 1,
                        width: 16,
                    })
                }
            })
        })

        return () => {

            layout.removeListener('layoutstop', onStop);
            cy.destroy();
            cyRef.current = null;
        }
        // eslint-disable-next-line
    }, []);

    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === 'Escape') {
                console.log('ESC pressed');
            }
            clear();
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, []);

    useEffect(() => {
        if (detailNode) {
            console.log("Detail Node: ", detailNode);
            if (detailNode.data.id.startsWith('y')) {
                setOptionKey('staff');
            }
            if (detailNode.data.id.startsWith('g')) {
                setOptionKey('research');
            }
            if (detailNode.data.id.startsWith('b')) {
                setOptionKey('teaching');
            }
        }
    }, [detailNode]);

    return <div
        style={{
            position: 'relative',    // ← step 1: establish a positioned parent
            width: '450px',
            height: '450px',
        }}
    >
        {/* Your Cytoscape container lives here */}
        <div id="cy" ref={containerRef} style={{width: '100%', height: '100%'}}/>

        {/* step 2: absolutely position it at top-left */}
        {/*<div style={{*/}
        {/*    position: 'absolute',*/}
        {/*    left: 0,*/}
        {/*    top: '50%',*/}
        {/*    transform: 'translateY(-50%)',*/}
        {/*    zIndex: 10,*/}
        {/*    padding: '8px',    // 可选，给点内边距*/}
        {/*}}>*/}
        {/*    <h3>Node Type</h3>*/}
        {/*    <Segmented*/}
        {/*        style={{*/}
        {/*            opacity: 0.8,*/}
        {/*        }}*/}
        {/*        options={[*/}
        {/*            {*/}
        {/*                label: (*/}
        {/*                    <div style={{padding: 4}}>*/}
        {/*                        <Avatar style={{backgroundColor: 'green'}}/>*/}
        {/*                        <div>Research</div>*/}
        {/*                    </div>*/}
        {/*                ),*/}
        {/*                value: 'research',*/}
        {/*                disabled: true*/}
        {/*            },*/}
        {/*            {*/}
        {/*                label: (*/}
        {/*                    <div style={{padding: 4}}>*/}
        {/*                        <Avatar style={{backgroundColor: 'grey'}}>Name</Avatar>*/}
        {/*                        <div>Staff</div>*/}
        {/*                    </div>*/}
        {/*                ),*/}
        {/*                value: 'staff',*/}
        {/*                disabled: true*/}
        {/*            },*/}
        {/*            {*/}
        {/*                label: (*/}
        {/*                    <div style={{padding: 4}}>*/}
        {/*                        <Avatar style={{backgroundColor: 'blue'}}>CourseID</Avatar>*/}
        {/*                        <div>Teaching</div>*/}
        {/*                    </div>*/}
        {/*                ),*/}
        {/*                value: 'teaching',*/}
        {/*                disabled: true*/}
        {/*            },*/}
        {/*        ]}*/}
        {/*        defaultValue="staff"*/}
        {/*        value={optionKey}*/}
        {/*        onChange={setOptionKey}*/}
        {/*        horizontal*/}
        {/*    />*/}
        {/*</div>*/}

        <div
            style={{
                position: 'absolute',      // 或者 fixed（见下）
                right: 0,
                top: 8,
                transform: 'none',         // 不需要 translateY 了
                transformOrigin: 'right top',
                zIndex: 10,
                padding: 0,
            }}
        >
            {/*<h4 style={{ margin: 0, fontSize: 12 }}>Node Type</h4>*/}

            <Segmented
                size="small"
                className="node-type-segmented"
                style={{opacity: 0.9}}
                options={[
                    {
                        label: (
                            <div style={{padding: 0, textAlign: 'center'}}>
                                <Avatar size={15} style={{backgroundColor: 'green'}}/>
                                <div style={{fontSize: 8, lineHeight: '14px'}}>Research</div>
                            </div>
                        ),
                        value: 'research',
                        disabled: true,
                    },
                    {
                        label: (
                            <div style={{padding: 0, textAlign: 'center'}}>
                                <Avatar size={15} style={{backgroundColor: 'grey'}}>N</Avatar>
                                <div style={{fontSize: 8, lineHeight: '14px'}}>Staff</div>
                            </div>
                        ),
                        value: 'staff',
                        disabled: true,
                    },
                    {
                        label: (
                            <div style={{padding: 0, textAlign: 'center'}}>
                                <Avatar size={15} style={{backgroundColor: 'blue'}}>ID</Avatar>
                                <div style={{fontSize: 8, lineHeight: '14px'}}>Teaching</div>
                            </div>
                        ),
                        value: 'teaching',
                        disabled: true,
                    },
                ]}
                defaultValue="staff"
                value={optionKey}
                onChange={setOptionKey}

                // 'horizontal' isn't a Segmented prop—it's horizontal by default
            />
        </div>
        <div
            //     style={{
            //     position: 'absolute',
            //     top:      0,       // 锁定到父容器顶部
            //     right:    0,       // 锁定到父容器右侧
            //     zIndex:   10,
            //     margin:   '25px',   // 可选：给点空隙不贴边
            // }}
        >
            {/*<h3>Node Details</h3>*/}
            {cyRef.current && <DetailDisplay node={detailNode} centerId={`c - ${jsonData.key}`}/>}
        </div>

        <AutoComplete
            style={{
                width: 200,
                position: 'absolute',
                top: 0,
                left: 0,
                zIndex: 10,          // ← step 3: ensure it floats above your graph
                margin: 8            // optional padding from the corner
            }}
            options={searchOptions}
            onSearch={handleSearchBarSearch}
            onSelect={handleSearchBarSelect}
            placeholder="Search by Name or ID"
            popupMatchSelectWidth={false}
        >
            <Input size="large" allowClear/>
        </AutoComplete>
    </div>

    // return <>
    //     <div style={{width: '800px', height: '800px'}} ref={containerRef}/>
    //
    //     <Segmented
    //         options={[
    //             {
    //                 label: (
    //                     <div style={{padding: 4}}>
    //                         <Avatar src="https://api.dicebear.com/7.x/miniavs/svg?seed=8"/>
    //                         <div>User 1</div>
    //                     </div>
    //                 ),
    //                 value: 'staff',
    //                 disabled:true
    //             },
    //             {
    //                 label: (
    //                     <div style={{padding: 4}}>
    //                         <Avatar style={{backgroundColor: '#f56a00'}}>K</Avatar>
    //                         <div>User 2</div>
    //                     </div>
    //                 ),
    //                 value: 'research',
    //                 disabled:true
    //             },
    //             {
    //                 label: (
    //                     <div style={{padding: 4}}>
    //                         <Avatar style={{backgroundColor: '#87d068'}} icon={<UserOutlined/>}/>
    //                         <div>User 3</div>
    //                     </div>
    //                 ),
    //                 value: 'teaching',
    //                 disabled:true
    //             },
    //         ]}
    //         defaultValue={'staff'}
    //         value={optionKey}
    //         onChange={setOptionKey}
    //         vertical={true}
    //     />
    // </>
};

export default ConcentricGraph;


